import type { InputFrame } from "../input";
import { surfaceAt, type QuarterInfo } from "../level";
import type { Skater } from "../skater";
import { airG } from "./air";
import type { AirState } from "./state";
import {
  SIDE_ASSIST,
  SIDE_LAND_ANGLE,
  SIDE_LAND_IN,
  SIDE_MAX_SPEED,
  SIDE_MIN_ANGLE,
  SIDE_SHIFT,
  SIDE_SHIFT_STEP,
  SIDE_STEP,
  SIDE_STICK,
} from "./tuning";

/**
 * Side transfers (#116): a vert launch carving along the coping toward
 * another ramp beside this one (a halfpipe wall next to a quarter, facing
 * the same way, with a gap between) carries over and comes down that ramp's
 * wall instead of straight back down its own.
 *
 * Called from `launchVert` once the air is set up as a plain vert air (speed
 * out over the deck killed, locked to the launch ramp's line). If the carve
 * heads that way (at least SIDE_MIN_ANGLE off the ramp's line) or the stick
 * is held that way, and the neighbour is in reach, this sets the speed along
 * the coping that lands the air SIDE_LAND_IN past the gap: the carve's own
 * speed along the coping when that's enough (never so much it overshoots the
 * neighbour), nudged up by at most SIDE_ASSIST without the stick, and never
 * past SIDE_MAX_SPEED. The landing is judged against the neighbour's line
 * like any vert air, and scores Transfer. Otherwise nothing changes: the
 * air keeps #82's forgiveness and comes back down its own ramp.
 */
export function sideTransfer(sk: Skater, q: QuarterInfo, input: InputFrame, travelSpeed: number): boolean {
  const air = sk.air!;
  // Along the coping, and how the carve and the stick lean along it.
  const cx = -q.axisZ;
  const cz = q.axisX;
  const f = sk.forward;
  const sign = sk.speed >= 0 ? 1 : -1;
  const carve = sign * (f.x * cx + f.z * cz); // sine of the heading off the ramp's line
  // Right on the stick is the rider's right (as on the ground), whichever way they're rolling.
  const stick = Math.abs(input.moveX) > SIDE_STICK ? Math.sign(input.moveX * (-f.z * cx + f.x * cz)) : 0;
  const carving = Math.abs(carve) >= Math.sin(SIDE_MIN_ANGLE);
  const dir = carving ? Math.sign(carve) : stick;
  if (dir === 0) return false;
  const pushed = stick === dir;
  const own = carving ? Math.abs(travelSpeed * carve) : 0;

  const g = airG(sk);
  const apex = sk.y + (sk.vy * sk.vy) / (2 * g);
  const fallTime = (h: number) => (sk.vy + Math.sqrt(Math.max(0, sk.vy * sk.vy + 2 * g * (sk.y - h)))) / g;
  const dx = cx * dir;
  const dz = cz * dir;
  const parallel = (o: QuarterInfo | undefined): o is QuarterInfo => !!o && o.axisX * q.axisX + o.axisZ * q.axisZ > 0.9;
  // A ramp beside this one needn't start level with it: at `d` along the
  // coping, the nearest spot up or down its line (at most SIDE_SHIFT away)
  // that's partway up its transition, and how far along the line (`k`, up it) that is.
  const rampAt = (d: number) => {
    for (let i = 0; i <= SIDE_SHIFT / SIDE_SHIFT_STEP; i++) {
      for (const k of i === 0 ? [0] : [i * SIDE_SHIFT_STEP, -i * SIDE_SHIFT_STEP]) {
        const s = surfaceAt(sk.level, sk.x + dx * d + q.axisX * k, sk.z + dz * d + q.axisZ * k);
        const o = s.quarter;
        if (parallel(o) && o.u >= 0.4 * o.r && o.u <= o.r * Math.sin(SIDE_LAND_ANGLE)) return { k, s, o };
      }
    }
    return null;
  };

  // Off the end of this ramp, across a gap nothing tall blocks, to the next one.
  const reach = SIDE_MAX_SPEED * fallTime(0);
  let gap = -1;
  let edge = -1;
  for (let d = SIDE_STEP; d <= reach; d += SIDE_STEP) {
    const s = surfaceAt(sk.level, sk.x + dx * d, sk.z + dz * d);
    if (s.h > apex - 0.3) return false;
    if (gap < 0) {
      if (!parallel(s.quarter)) gap = d;
    } else if (rampAt(d)) {
      edge = d;
      break;
    }
  }
  if (edge < 0) return false;

  // Where to come down: SIDE_LAND_IN onto it, partway up its transition.
  const target = edge + SIDE_LAND_IN;
  const land = rampAt(target);
  if (!land) return false;
  const t = fallTime(land.s.h);
  const needed = target / t;
  // As far as the carve's own speed carries, but not within SIDE_LAND_IN of the far end.
  let far = target;
  while (far + SIDE_STEP <= own * t + SIDE_LAND_IN && rampAt(far + SIDE_STEP)) far += SIDE_STEP;
  let v = Math.min(own, (far - SIDE_LAND_IN) / t, SIDE_MAX_SPEED);
  if (v < needed) {
    if (needed > SIDE_MAX_SPEED || (!pushed && needed - own > SIDE_ASSIST)) return false;
    v = needed;
  }

  // That speed along the coping, and a drift along the line onto the spot.
  const along = sk.vx * cx + sk.vz * cz;
  const up = sk.vx * q.axisX + sk.vz * q.axisZ;
  const drift = land.k / t;
  sk.vx += (dir * v - along) * cx + (drift - up) * q.axisX;
  sk.vz += (dir * v - along) * cz + (drift - up) * q.axisZ;
  air.vertAxis = { x: land.o.axisX, z: land.o.axisZ };
  air.spineLip = null;
  air.side = { x: sk.x, z: sk.z, dx, dz, edge };
  return true;
}

/** Whether a side transfer air (#116) is coming down past the gap it was aimed across. */
export function sideLanded(air: AirState, x: number, z: number): boolean {
  const s = air.side;
  return !!s && (x - s.x) * s.dx + (z - s.z) * s.dz >= s.edge - SIDE_STEP;
}
